(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bdk-category-hub]';
  const NAVIGATION_SELECTOR = '[data-bdk-local-nav-scroll]';
  const NAVIGATION_VIEWPORT_SELECTOR = '[data-bdk-local-nav-viewport]';
  const NAVIGATION_PREV_SELECTOR = '[data-bdk-local-nav-prev]';
  const NAVIGATION_NEXT_SELECTOR = '[data-bdk-local-nav-next]';
  const NAVIGATION_ACTIVE_SELECTOR = '[data-bdk-local-nav-active], .subcollections-inline__link.is-active';
  const NAVIGATION_SHELL_SELECTOR = '[data-bdk-hub-navigation-shell]';
  const STICKY_SENTINEL_SELECTOR = '[data-bdk-hub-sticky-sentinel]';

  const desktopControls = window.matchMedia('(min-width: 990px)');
  const mobileNavigation = window.matchMedia('(max-width: 989px)');

  let stickyShell = null;
  let stickySentinel = null;
  let stickyFrame = null;

  const getStickyOffset = () => {
    if (!document.querySelector('[data-header-sticky]')) return 0;

    const styles = window.getComputedStyle(document.documentElement);
    const announcementHeight = Number.parseFloat(styles.getPropertyValue('--announcement-height')) || 0;
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
    return announcementHeight + headerHeight;
  };

  const resetStickyNavigation = () => {
    stickyShell?.classList.remove('is-sticky');
    stickyShell?.style.removeProperty('--bdk-hub-sticky-top');
    if (stickySentinel) stickySentinel.style.height = '0px';
  };

  const syncStickyNavigation = (root) => {
    const shell = root?.querySelector(NAVIGATION_SHELL_SELECTOR);

    if (!shell) {
      resetStickyNavigation();
      stickyShell = null;
      stickySentinel = null;
      return;
    }

    if (stickyShell !== shell) {
      resetStickyNavigation();
      stickyShell = shell;
      stickySentinel = shell.previousElementSibling?.matches(STICKY_SENTINEL_SELECTOR)
        ? shell.previousElementSibling
        : document.createElement('div');

      if (!stickySentinel.matches(STICKY_SENTINEL_SELECTOR)) {
        stickySentinel.setAttribute('data-bdk-hub-sticky-sentinel', '');
        stickySentinel.setAttribute('aria-hidden', 'true');
        shell.before(stickySentinel);
      }
    }

    const stickyOffset = getStickyOffset();
    const sentinelTop = stickySentinel.getBoundingClientRect().top;
    const isSticky = shell.classList.contains('is-sticky');
    const shouldStick = window.scrollY > 0 && sentinelTop <= stickyOffset;
    const shellHeight = shell.getBoundingClientRect().height;

    shell.style.setProperty('--bdk-hub-sticky-top', `${stickyOffset}px`);

    if (shouldStick === isSticky) {
      if (shouldStick) {
        const sentinelHeight = Number.parseFloat(stickySentinel.style.height) || 0;
        if (Math.abs(sentinelHeight - shellHeight) > 0.1) {
          stickySentinel.style.height = `${shellHeight}px`;
        }
      }
      return;
    }

    if (shouldStick) {
      stickySentinel.style.height = `${shellHeight}px`;
      shell.classList.add('is-sticky');
    } else {
      shell.classList.remove('is-sticky');
      stickySentinel.style.height = '0px';
    }
  };

  const scheduleStickyNavigation = () => {
    if (stickyFrame) return;

    stickyFrame = window.requestAnimationFrame(() => {
      stickyFrame = null;
      syncStickyNavigation(document.querySelector(ROOT_SELECTOR));
    });
  };

  const getScrollState = (track) => {
    const overflow = track.scrollWidth > track.clientWidth + 1;
    const atStart = track.scrollLeft <= 1;
    const atEnd = Math.ceil(track.scrollLeft + track.clientWidth) >= track.scrollWidth - 1;

    return { overflow, atStart, atEnd };
  };

  const updateNavigation = (navigation) => {
    const viewport = navigation.closest(NAVIGATION_VIEWPORT_SELECTOR);
    const previous = viewport?.querySelector(NAVIGATION_PREV_SELECTOR);
    const next = viewport?.querySelector(NAVIGATION_NEXT_SELECTOR);
    const { overflow, atStart, atEnd } = getScrollState(navigation);

    navigation.classList.toggle('has-overflow', overflow);
    navigation.classList.toggle('is-start', overflow && atStart);
    navigation.classList.toggle('is-end', overflow && atEnd);

    if (previous) previous.hidden = !desktopControls.matches || !overflow || atStart;
    if (next) next.hidden = !desktopControls.matches || !overflow || atEnd;
  };

  const centerActiveNavigation = (navigation) => {
    if (!mobileNavigation.matches) {
      delete navigation.dataset.bdkHubNavigationCentered;
      return;
    }

    if (navigation.dataset.bdkHubNavigationCentered === 'true') return;

    const active = navigation.querySelector(NAVIGATION_ACTIVE_SELECTOR);
    navigation.dataset.bdkHubNavigationCentered = 'true';
    if (!active) return;

    const target = Math.max(
      0,
      Math.min(
        active.offsetLeft + active.offsetWidth / 2 - navigation.clientWidth / 2,
        navigation.scrollWidth - navigation.clientWidth
      )
    );

    navigation.scrollTo({ left: target, behavior: 'auto' });
    updateNavigation(navigation);
  };

  const bindNavigation = (navigation) => {
    const viewport = navigation.closest(NAVIGATION_VIEWPORT_SELECTOR);
    if (!viewport) return;

    const previous = viewport.querySelector(NAVIGATION_PREV_SELECTOR);
    const next = viewport.querySelector(NAVIGATION_NEXT_SELECTOR);

    if (navigation.dataset.bdkHubNavigationBound !== 'true') {
      navigation.dataset.bdkHubNavigationBound = 'true';
      navigation.addEventListener('scroll', () => updateNavigation(navigation), { passive: true });
    }

    if (viewport.dataset.bdkHubNavigationControlsBound !== 'true') {
      viewport.dataset.bdkHubNavigationControlsBound = 'true';

      previous?.addEventListener('click', () => {
        navigation.scrollBy({
          left: -Math.max(240, Math.round(navigation.clientWidth * 0.72)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });

      next?.addEventListener('click', () => {
        navigation.scrollBy({
          left: Math.max(240, Math.round(navigation.clientWidth * 0.72)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    }

    updateNavigation(navigation);
    centerActiveNavigation(navigation);
  };


  const initRoot = (root) => {
    root.querySelectorAll(NAVIGATION_SELECTOR).forEach(bindNavigation);
    syncStickyNavigation(root);
  };

  const initAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) {
      initRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const refreshAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.querySelectorAll(NAVIGATION_SELECTOR).forEach((navigation) => {
        delete navigation.dataset.bdkHubNavigationCentered;
        updateNavigation(navigation);
        centerActiveNavigation(navigation);
      });
    });

    scheduleStickyNavigation();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  window.addEventListener('scroll', scheduleStickyNavigation, { passive: true });
  window.addEventListener('resize', refreshAll, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  desktopControls.addEventListener?.('change', refreshAll);
  mobileNavigation.addEventListener?.('change', refreshAll);

  if (document.fonts?.ready) {
    document.fonts.ready.then(refreshAll);
  }
})();
